/**
 * TournamentHelpers.jsx
 * Shared small UI components used in TournamentDetailPage:
 *   DatePickerField, TimePickerField, StatusBadge, TeamFormRowEdit, SeedAssignPanel
 */
import { useState, useEffect, useRef } from 'react';
import { CalendarDays, ChevronDown, Clock, Play, CheckCircle2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { toast } from 'sonner';

// ── Calendar classes ──────────────────────────────────────────────────────────
const CALENDAR_CLASSES = {
  months: 'flex flex-col space-y-4',
  month: 'space-y-4',
  caption: 'flex justify-center pt-1 relative items-center',
  caption_label: 'text-sm font-medium text-[#f0f2f7]',
  nav: 'space-x-1 flex items-center',
  nav_button: 'h-7 w-7 p-0 text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#252a37] rounded-md border-0 bg-transparent inline-flex items-center justify-center',
  nav_button_previous: 'absolute left-1',
  nav_button_next: 'absolute right-1',
  table: 'w-full border-collapse',
  head_row: 'flex',
  head_cell: 'text-[#8a93a3] rounded-md w-9 font-normal text-[0.8rem] text-center',
  row: 'flex w-full mt-2',
  cell: 'h-9 w-9 text-center text-sm p-0',
  day: 'h-9 w-9 p-0 font-normal text-[#f0f2f7] rounded-md hover:bg-[#252a37] inline-flex items-center justify-center',
  day_selected: 'bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] hover:text-[#0b0f18] font-bold',
  day_today: 'bg-[#1d2230] text-[#f5c518] font-semibold',
  day_outside: 'text-[#8a93a3]/30 hover:bg-transparent',
  day_disabled: 'text-[#8a93a3]/20 hover:bg-transparent',
  day_hidden: 'invisible',
};

// ── DatePickerField ───────────────────────────────────────────────────────────
export function DatePickerField({ value, onChange, placeholder = 'Pick a date', testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + 'T00:00') : undefined;
  const display  = selected
    ? selected.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testId}
          className={`w-full justify-start text-left font-normal bg-[#1d2230] border-[#252a37] hover:bg-[#252a37] hover:text-[#f0f2f7] ${display ? 'text-[#f0f2f7]' : 'text-[#8a93a3]'}`}>
          <CalendarDays className="h-4 w-4 mr-2 flex-shrink-0" />
          {display || placeholder}
          <ChevronDown className="h-4 w-4 ml-auto opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#10131c] border-[#252a37] shadow-2xl" align="start">
        <CalendarPicker
          mode="single" selected={selected} initialFocus classNames={CALENDAR_CLASSES}
          onSelect={day => {
            if (day) {
              const yyyy = day.getFullYear();
              const mm   = String(day.getMonth()+1).padStart(2,'0');
              const dd   = String(day.getDate()).padStart(2,'0');
              onChange(`${yyyy}-${mm}-${dd}`);
            }
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

// ── TimePickerField ───────────────────────────────────────────────────────────
const HOURS   = Array.from({ length: 18 }, (_, i) => {
  const h = i + 6;
  const label = h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h-12}:00 PM`;
  return { value: String(h).padStart(2,'0'), label };
});
const MINUTES = ['00','15','30','45'];

export function TimePickerField({ value, onChange, testId }) {
  const [h, m] = value ? value.split(':') : ['',''];
  return (
    <div className="flex gap-2" data-testid={testId}>
      <Select value={h||''} onValueChange={v => onChange(`${v}:${m||'00'}`)}>
        <SelectTrigger className="flex-1 bg-[#1d2230] border-[#252a37] text-[#f0f2f7]">
          <Clock className="h-3.5 w-3.5 mr-2 text-[#8a93a3]" />
          <SelectValue placeholder="Hour" />
        </SelectTrigger>
        <SelectContent className="bg-[#161a26] border-[#252a37] max-h-[200px]">
          {HOURS.map(({ value: v, label }) => (
            <SelectItem key={v} value={v} className="text-[#f0f2f7] focus:bg-[#1d2230]">{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={m||''} onValueChange={v => onChange(`${h||'09'}:${v}`)}>
        <SelectTrigger className="w-[90px] bg-[#1d2230] border-[#252a37] text-[#f0f2f7]">
          <SelectValue placeholder="Min" />
        </SelectTrigger>
        <SelectContent className="bg-[#161a26] border-[#252a37]">
          {MINUTES.map(min => (
            <SelectItem key={min} value={min} className="text-[#f0f2f7] focus:bg-[#1d2230]">:{min}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── StatusBadge ───────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', icon: Clock,          cls: 'text-[#3b82f6] border-[rgba(59,130,246,0.35)] bg-[rgba(59,130,246,0.1)]' },
  live:      { label: 'LIVE',      icon: Play,           cls: 'text-[#ef4444] border-[rgba(239,68,68,0.35)] bg-[rgba(239,68,68,0.1)]' },
  completed: { label: 'Done',      icon: CheckCircle2,   cls: 'text-[#22c55e] border-[rgba(34,197,94,0.35)] bg-[rgba(34,197,94,0.1)]' },
  finished:  { label: 'Done',      icon: CheckCircle2,   cls: 'text-[#22c55e] border-[rgba(34,197,94,0.35)] bg-[rgba(34,197,94,0.1)]' },
};

export function StatusBadge({ status }) {
  const cfg  = STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.scheduled;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.cls} border text-xs flex items-center gap-1 w-fit`}>
      <Icon className="h-3 w-3" />{cfg.label}
    </Badge>
  );
}

export const formatRound = (round) => {
  const LABELS = {
    rr_r1:'Round 1',rr_r2:'Round 2',rr_r3:'Round 3',rr_r4:'Round 4',
    rr_r5:'Round 5',rr_r6:'Round 6',rr_r7:'Round 7',
    qf:'Quarter Finals',sf:'Semi Finals',f:'Final','3rd':'3rd Place',
    sf_m1:'Semi Final 1',sf_m2:'Semi Final 2',sf_m3:'Semi Final 3',sf_m4:'Semi Final 4',
    final_m3:'Final',final_m5:'Final',
  };
  return LABELS[round] || round?.replace(/_/g,' ') || 'Round';
};

// ── TeamFormRowEdit ───────────────────────────────────────────────────────────
export function TeamFormRowEdit({ idx, data, onChange }) {
  const fileRef = useRef(null);
  const autoCode = (n) => n.split(/\s+/).map(w=>w[0]||'').join('').toUpperCase().slice(0,5) || n.slice(0,4).toUpperCase();
  const handleLogo = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(idx, { ...data, logo_b64: ev.target.result, logo_preview: ev.target.result });
    reader.readAsDataURL(file);
  };
  return (
    <div className="flex items-center gap-2 p-2.5 bg-[#1d2230] rounded-lg border border-[#252a37]">
      <span className="w-5 h-5 rounded-full bg-[#252a37] flex items-center justify-center text-[10px] text-[#8a93a3] flex-shrink-0 font-bold">{idx+1}</span>
      <div onClick={() => fileRef.current?.click()}
        className="h-9 w-9 rounded-lg cursor-pointer overflow-hidden flex items-center justify-center border border-[#252a37] hover:border-[#f5c518] flex-shrink-0"
        style={{ backgroundColor: data.color }} title="Click to upload logo">
        {data.logo_preview
          ? <img src={data.logo_preview} className="h-full w-full object-cover" alt="" />
          : <Upload className="h-3.5 w-3.5 text-white/60" />}
      </div>
      <input type="file" accept="image/*" ref={fileRef} hidden onChange={handleLogo} />
      <input type="color" value={data.color} onChange={e => onChange(idx, { ...data, color: e.target.value })}
        className="h-8 w-8 rounded cursor-pointer p-0.5 border-0 bg-transparent flex-shrink-0" />
      <input value={data.name} placeholder={`Team ${idx+1} name *`}
        onChange={e => {
          const name = e.target.value;
          const auto = !data.short_name || data.short_name === autoCode(data.name);
          onChange(idx, { ...data, name, short_name: auto ? autoCode(name) : data.short_name });
        }}
        className="flex-1 h-8 bg-[#161a26] border border-[#252a37] text-[#f0f2f7] text-sm rounded-md px-2 focus:outline-none focus:border-[rgba(245,197,24,0.4)] min-w-0" />
      <input value={data.short_name} placeholder="CODE" maxLength={5}
        onChange={e => onChange(idx, { ...data, short_name: e.target.value.toUpperCase().slice(0,5) })}
        className="w-14 h-8 bg-[#161a26] border border-[#252a37] text-[#f0f2f7] text-xs rounded-md px-2 text-center uppercase focus:outline-none focus:border-[rgba(245,197,24,0.4)]" />
      <input value={data.sponsor} placeholder="Sponsor name (optional)"
        onChange={e => onChange(idx, { ...data, sponsor: e.target.value })}
        className="w-36 h-8 bg-[#161a26] border border-[#252a37] text-[#f0f2f7] text-xs rounded-md px-2 focus:outline-none focus:border-[rgba(245,197,24,0.4)] hidden sm:block" />
    </div>
  );
}

// ── SeedAssignPanel ───────────────────────────────────────────────────────────
export function SeedAssignPanel({ tournamentId, orgSlug, orgTeams, onAssigned }) {
  const token = () => localStorage.getItem('btccp_access_token');
  const [encounters, setEncounters] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/encounters`,
      { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setEncounters(Array.isArray(data) ? data : []);
        const init = {};
        data.forEach(e => {
          if (e.home_seed && e.home_team_id) init[e.home_seed] = e.home_team_id;
          if (e.away_seed && e.away_team_id) init[e.away_seed] = e.away_team_id;
        });
        setAssignments(init);
      });
  }, [tournamentId, orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const seeds = {};
  encounters.filter(e => e.round_type === 'group').forEach(e => {
    if (e.home_seed) seeds[e.home_seed] = { seed: e.home_seed, letter: e.home_seed[0] };
    if (e.away_seed) seeds[e.away_seed] = { seed: e.away_seed, letter: e.away_seed[0] };
  });
  const seedList = Object.values(seeds).sort((a,b) => a.seed.localeCompare(b.seed));
  const groups   = [...new Set(seedList.map(s=>s.letter))].sort();
  const allAssigned = seedList.every(s => assignments[s.seed]);

  const saveAll = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(assignments).filter(([,v])=>v);
      await Promise.all(entries.map(([seed, teamId]) =>
        fetch(`/api/orgs/${orgSlug}/tournaments/${tournamentId}/assign-seed`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ seed, team_id: teamId }),
        })
      ));
      toast.success(`${entries.length} seeds assigned to teams`);
      onAssigned?.();
    } catch (_) { toast.error('Failed to save assignments'); }
    finally { setSaving(false); }
  };

  if (seedList.length === 0) return null;

  return (
    <div className="border-t border-[#252a37] pt-3">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center justify-between w-full text-left group">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#f0f2f7]">Assign Teams to Seeds</span>
          {allAssigned
            ? <span className="text-[10px] text-[#22c55e] bg-[rgba(34,197,94,0.1)] px-2 py-0.5 rounded-full">All assigned</span>
            : <span className="text-[10px] text-[#f5c518] bg-[rgba(245,197,24,0.1)] px-2 py-0.5 rounded-full">
                {seedList.filter(s=>assignments[s.seed]).length}/{seedList.length} assigned
              </span>}
        </div>
        <span className="text-[#8a93a3] text-xs">{expanded ? '▲ Hide' : '▼ Show'}</span>
      </button>

      {expanded && (
        <div className="mt-3 space-y-4">
          {groups.map(letter => (
            <div key={letter}>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-5 w-5 rounded-full flex items-center justify-center font-black text-xs text-black"
                  style={{ background: letter==='A'?'#f5c518':'#3b82f6' }}>
                  {letter}
                </div>
                <p className="text-sm font-bold text-[#f0f2f7]">Group {letter}</p>
              </div>
              <div className="space-y-1.5">
                {seedList.filter(s => s.letter === letter).map(({ seed }) => {
                  const takenByOthers = new Set(
                    Object.entries(assignments).filter(([s,id]) => s !== seed && id).map(([,id]) => id)
                  );
                  const assignedTeam   = orgTeams.find(t => t.id === assignments[seed]);
                  const availableTeams = orgTeams.filter(t => !takenByOthers.has(t.id));
                  return (
                    <div key={seed} data-testid={`seed-row-${seed}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl border transition-colors ${assignedTeam ? 'bg-[#1a1f2e] border-[rgba(245,197,24,0.3)]' : 'bg-[#1d2230] border-[#252a37]'}`}>
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0"
                        style={{ background: assignedTeam?.color ? `${assignedTeam.color}25` : 'rgba(245,197,24,0.1)',
                                 color: assignedTeam?.color || '#f5c518',
                                 border: `1.5px solid ${assignedTeam?.color || '#f5c518'}50` }}>
                        {seed}
                      </div>
                      <span className="text-[#8a93a3] flex-shrink-0 text-base">→</span>
                      <div className="flex-1 min-w-0">
                        <select value={assignments[seed]||''} data-testid={`seed-select-${seed}`}
                          onChange={e => setAssignments(prev => ({ ...prev, [seed]: e.target.value }))}
                          className="w-full bg-[#10131c] border border-[#252a37] rounded-lg px-3 py-2 text-sm text-[#f0f2f7] focus:outline-none focus:border-[#f5c518] cursor-pointer">
                          <option value="">— Select team for {seed} —</option>
                          {availableTeams.length === 0 && orgTeams.length > 0
                            ? <option disabled>All teams assigned</option>
                            : availableTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      {assignedTeam && (
                        <span className="text-[10px] font-semibold flex-shrink-0 px-2 py-0.5 rounded-full"
                          style={{ backgroundColor:`${assignedTeam.color}20`, color: assignedTeam.color||'#22c55e', border:`1px solid ${assignedTeam.color}40` }}>
                          ✓
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 border-t border-[#252a37]">
            <p className="text-xs text-[#8a93a3]">{seedList.filter(s=>assignments[s.seed]).length} of {seedList.length} seeds assigned</p>
            <Button size="sm" onClick={saveAll} disabled={saving} data-testid="save-seed-assignments-button"
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a] h-8 text-sm px-4 font-bold">
              {saving ? 'Saving...' : '💾 Save All Assignments'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
